import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemText, ListItemIcon, Container, Button, Box, Modal } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TripIcon from '@mui/icons-material/TravelExplore';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import GroupIcon from '@mui/icons-material/Group';
import SyncIcon from '@mui/icons-material/Sync';
import { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from './utils/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import theme from './utils/theme';
import './App.css';
import Register from './components/Register';
import Admin from './components/Admin';
import AdminUserManagement from './components/AdminUserManagement';
import ExpenseSplitter from './modules/expense-splitter/ExpenseSplitter';
import TripManager from './modules/trip-manager/TripManager';
import RegistrationRequest from './components/RegistrationRequest';
import Footer from './components/Footer';

// Animated Feature Carousel Component
const FeatureCarousel = () => {
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      icon: FlightTakeoffIcon,
      title: 'Trip-Based Expense Splitting',
      description: 'Create trips, add participants, and split expenses fairly among group members with multiple splitting methods.',
      color: '#6366f1'
    },
    {
      icon: GroupIcon,
      title: 'Admin Management',
      description: 'Powerful admin tools to manage users, trips, and access permissions with comprehensive user controls.',
      color: '#8b5cf6'
    },
    {
      icon: SyncIcon,
      title: 'Real-time Collaboration',
      description: 'Work together seamlessly with Firebase-powered real-time updates and instant synchronization.',
      color: '#06b6d4'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4, p: 2 }}>
      <Box
        sx={{
          position: 'relative',
          height: 340,
          overflow: 'hidden',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        {features.map((feature, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              opacity: index === currentFeature ? 1 : 0,
              visibility: index === currentFeature ? 'visible' : 'hidden',
              transition: 'opacity 0.8s ease-in-out, visibility 0.8s ease-in-out',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                backgroundColor: feature.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              <feature.icon sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#1e293b', 
                mb: 2, 
                fontWeight: 600, 
                textAlign: 'center',
                fontSize: '1.5rem',
                letterSpacing: '-0.025em'
              }}
            >
              {feature.title}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748b', 
                textAlign: 'center', 
                maxWidth: 400, 
                lineHeight: 1.6, 
                fontSize: '1rem',
                fontWeight: 400
              }}
            >
              {feature.description}
            </Typography>
          </Box>
        ))}

        {/* Dots indicator */}
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1.5 }}>
          {features.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: index === currentFeature ? '#6366f1' : '#e2e8f0',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: index === currentFeature ? '#4f46e5' : '#cbd5e1',
                  transform: 'scale(1.2)',
                }
              }}
              onClick={() => {
                console.log('Clicked dot', index);
                setCurrentFeature(index);
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Placeholder components
const Home = ({ user, userModules, isAdmin, onSignIn }) => {
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

  // Show welcome modal when user logs in
  useEffect(() => {
    if (user && !welcomeModalOpen) {
      setWelcomeModalOpen(true);
    }
  }, [user, welcomeModalOpen]);

  const content = (
    <>
      {/* Welcome Header - Only for non-logged-in users */}
      {!user && (
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
            Welcome to BonStudio
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Smart expense management for groups and teams
          </Typography>
        </Box>
      )}

      {/* Feature Showcase - Only for non-logged-in users */}
      {!user && (
        <Box sx={{ mb: 4 }}>
          <FeatureCarousel />
        </Box>
      )}

      {user ? (
        // Logged in users - Simple welcome
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
            Welcome back, {user.displayName}!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Ready to manage your expenses?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setWelcomeModalOpen(true)}
            size="large"
          >
            Get Started
          </Button>
        </Box>
      ) : (
        // Unknown users - modern landing page
        <>
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Prominent CTA Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mb: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onSignIn}
                fullWidth
                sx={{ maxWidth: 300 }}
              >
                Sign In with Google
              </Button>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                New user?{' '}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: 'primary.dark' }
                  }}
                  onClick={() => setRegistrationModalOpen(true)}
                >
                  Register
                </Typography>
              </Typography>
            </Box>
          </Box>

        </>
      )}

      {/* Registration Modal */}
      <Modal
        open={registrationModalOpen}
        onClose={() => setRegistrationModalOpen(false)}
        aria-labelledby="registration-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            width: '100%'
          }}
        >
          <Typography id="registration-modal-title" variant="h6" component="h2" gutterBottom sx={{ color: 'primary.main', textAlign: 'center' }}>
            Get Started Today
          </Typography>
          <RegistrationRequest />
        </Box>
      </Modal>

      {/* Welcome Modal for Logged-in Users */}
      <Modal
        open={welcomeModalOpen}
        onClose={() => setWelcomeModalOpen(false)}
        aria-labelledby="welcome-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxWidth: 500,
            width: '100%'
          }}
        >
          <Typography id="welcome-modal-title" variant="h5" component="h2" gutterBottom sx={{ color: 'primary.main', textAlign: 'center', mb: 2 }}>
            Welcome, {user?.displayName}!
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}>
            {isAdmin ? 'What would you like to do today?' : 'Choose an action to get started:'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isAdmin ? (
              <>
                <Button variant="contained" color="primary" component={Link} to="/trip-manager" onClick={() => setWelcomeModalOpen(false)} fullWidth>
                  ✈️ Manage Trips
                </Button>
                <Button variant="contained" color="primary" component={Link} to="/expense-splitter" onClick={() => setWelcomeModalOpen(false)} fullWidth>
                  💰 Split Expenses
                </Button>
                <Button variant="contained" color="secondary" component={Link} to="/admin/user-management" onClick={() => setWelcomeModalOpen(false)} fullWidth>
                  👥 User Management
                </Button>
                <Button variant="contained" color="secondary" component={Link} to="/admin" onClick={() => setWelcomeModalOpen(false)} fullWidth>
                  ⚙️ Admin Panel
                </Button>
              </>
            ) : userModules.includes('expense-splitter') ? (
              <Button variant="contained" color="primary" component={Link} to="/expense-splitter" onClick={() => setWelcomeModalOpen(false)} fullWidth>
                💰 Split Expenses
              </Button>
            ) : (
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                You don't have access to any modules yet. Contact an admin to get started.
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );

  return (
    <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '70vh', py: 4, textAlign: 'center' }}>
      {content}
    </Container>
  );
};

function App() {
  const [user] = useAuthState(auth);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userModules, setUserModules] = useState([]);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  useEffect(() => {
    const checkUserAccess = async () => {
      if (user) {
        // Check if admin
        const adminQ = query(collection(db, 'admins'), where('email', '==', user.email));
        const adminSnapshot = await getDocs(adminQ);
        setIsAdmin(!adminSnapshot.empty);
        
        // Check modules
        const modules = [];
        const q = query(collection(db, 'modules/expense-splitter/users'), where('uid', '==', user.uid));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          modules.push('expense-splitter');
        }
        setUserModules(modules);
      } else {
        setIsAdmin(false);
        setUserModules([]);
      }
    };
    checkUserAccess();
  }, [user]);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  const menuItems = [
    { text: 'Home', path: '/', icon: HomeIcon, always: true },
    ...(isAdmin ? [
      { text: 'Trip Manager', path: '/trip-manager', icon: TripIcon },
      { text: 'Expense Splitter', path: '/expense-splitter', icon: AccountBalanceWalletIcon },
      { text: 'User Management', path: '/admin/user-management', icon: PeopleIcon },
      { text: 'Admin', path: '/admin', icon: AdminPanelSettingsIcon }
    ] : []),
    ...(!isAdmin && userModules.includes('expense-splitter') ? [{ text: 'Expense Splitter', path: '/expense-splitter', icon: AccountBalanceWalletIcon }] : []),
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppBar position="fixed">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              BonStudio
            </Typography>
            {user ? (
              <IconButton color="inherit" sx={{ color: 'white' }} onClick={() => auth.signOut()}>
                <LogoutIcon />
              </IconButton>
            ) : (
              <Typography variant="body1">Not logged in</Typography>
            )}
          </Toolbar>
        </AppBar>
        <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
          <List sx={{ width: 250 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} component={Link} to={item.path} onClick={toggleDrawer}>
                <ListItemIcon sx={{ color: '#2E5984' }}>
                  <item.icon />
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Drawer>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pt: 8 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Routes>
              <Route path="/" element={<Home user={user} userModules={userModules} isAdmin={isAdmin} onSignIn={signInWithGoogle} />} />
              <Route path="/register" element={<Register />} />
              <Route path="/expense-splitter" element={<ExpenseSplitter />} />
              <Route path="/trip-manager" element={<TripManager />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/user-management" element={<AdminUserManagement />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
